// src/app/theme/applyThemeToDom.ts
import type { ITheme } from '@vanialabs/ui-engine';

/**
 * applyThemeToDom
 * ------------------------
 * Explicit token -> CSS variable mapping. Deliberately NOT a generic
 * recursive flattener over `theme.tokens` — every variable the app
 * relies on is listed by name here, once, so:
 *   - adding a variable is a one-line addition, not a walker change
 *   - a typo/rename in the tokens contract surfaces as a type error
 *     on this file instead of silently renaming a CSS var in prod
 *   - nothing under `theme.tokens` is exposed to the DOM that this
 *     mapping didn't explicitly choose to expose
 *
 * `--vania-accent-violet/teal/rose/amber` and `--vania-border-glass`
 * read from `colors.extended` (the contract's named escape hatch for
 * theme-specific colors beyond the base token set). The default theme
 * does not currently populate `extended`, so those five resolve to
 * `undefined` and are left unset — the app's existing
 * `var(--vania-x, <fallback>)` usages already cover that case. See
 * remaining-work notes returned with this change.
 */
function buildCssVarMap(theme: ITheme): Record<string, string | undefined> {
  const { colors, radius, shadows, spacing, zIndex } = theme.tokens;
  const extended = colors.extended ?? {};

  return {
    '--vania-text-primary': colors.text.primary,
    '--vania-text-secondary': colors.text.secondary,
    '--vania-text-tertiary': colors.text.tertiary,

    '--vania-surface': colors.surface.primary,
    '--vania-surface-1': colors.surface.secondary,
    '--vania-surface-2': colors.surface.elevated,

    '--vania-border': colors.border.default,
    '--vania-border-glass': extended.borderGlass ?? colors.border.subtle,

    '--vania-accent': colors.action.primary,
    '--vania-accent-violet': extended.accentViolet,
    '--vania-accent-teal': extended.accentTeal,
    '--vania-accent-rose': extended.accentRose,
    '--vania-accent-amber': extended.accentAmber,

    '--vania-radius-lg': radius.lg,
    '--vania-radius-md': radius.md,
    '--vania-space-1': spacing.xs,
    '--vania-space-2': spacing.sm,
    '--vania-space-3': spacing.md,
    '--vania-space-4': spacing.lg,
    '--vania-space-5': spacing.xl,
    '--vania-space-6': spacing.xxl,
    '--vania-shadow-2': shadows.elevation2,
    '--vania-shadow-3': shadows.elevation3,

    '--vania-colors-background-primary': colors.background.primary,

    // Semantic UI Engine variables consumed by reusable primitives.
    '--color-surface-command': colors.surface.elevated,
    '--color-surface-glass': colors.surface.primary,
    '--color-accent-primary': colors.action.primary,
    '--color-border-subtle': extended.borderGlass ?? colors.border.subtle,
    '--color-text-primary': colors.text.primary,
    '--color-text-secondary': colors.text.secondary,
    '--color-feedback-success': colors.feedback.success,
    '--color-feedback-warning': colors.feedback.warning,
    '--color-feedback-error': colors.feedback.error,
    '--color-shadow-command': shadows.elevation3,
    '--color-chart-primary': colors.action.primaryHover,
    '--color-chart-secondary': colors.action.primary,
    '--color-chart-muted': colors.border.subtle,
    '--color-chart-surface': colors.background.tertiary,

    '--vania-z-content': String(zIndex.content),
    '--vania-z-header': String(zIndex.header),
    '--vania-z-dropdown': String(zIndex.dropdown),
    '--vania-z-modal': String(zIndex.modal),
    '--vania-z-toast': String(zIndex.toast),
  };
}

let suppressStyleEl: HTMLStyleElement | null = null;
let restoreRafId: number | null = null;

/**
 * Disables CSS transitions/animations document-wide for the moment the
 * token variables are swapped, then re-enables them a couple of frames
 * later once the new values are already the resting state.
 *
 * Without this, any existing `transition: ... 0.2s` rule that reads a
 * `--vania-*` custom property will animate from the old theme's color
 * to the new one, which is what shows up as a one-frame "flash" of the
 * previous theme. This never edits an existing CSS file -- it inserts
 * one ephemeral, empty-by-default `<style>` tag at runtime and removes
 * it again, scoped only to the duration of the swap.
 */
function suppressTransitionsDuringSwap(root: HTMLElement): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  if (restoreRafId !== null) {
    cancelAnimationFrame(restoreRafId);
    restoreRafId = null;
  }

  if (!suppressStyleEl) {
    suppressStyleEl = document.createElement('style');
    suppressStyleEl.setAttribute('data-theme-transition-suppressor', 'true');
    suppressStyleEl.textContent = `
      *, *::before, *::after {
        transition-property: none !important;
        animation-duration: 0s !important;
      }
    `;
  }
  if (!suppressStyleEl.isConnected) {
    document.head.appendChild(suppressStyleEl);
  }

  // Force the browser to flush style recalculation now, so the
  // suppression rule is guaranteed to be in effect before the CSS
  // variables below change.
  void root.offsetHeight;

  return () => {
    // Wait two frames: one for the browser to have painted with
    // transitions off and the new variables in place, one more as a
    // safety margin, then lift the suppression so future, deliberate
    // (e.g. hover) transitions keep working normally.
    restoreRafId = requestAnimationFrame(() => {
      restoreRafId = requestAnimationFrame(() => {
        suppressStyleEl?.remove();
        restoreRafId = null;
      });
    });
  };
}

/**
 * Applies the given theme's tokens to a DOM element's inline style as
 * CSS custom properties (defaults to the document root). Variables
 * whose mapped token is missing are cleared with `removeProperty`
 * rather than written as `"undefined"`, so any `var(--x, fallback)`
 * fallback in existing CSS/inline styles keeps working.
 *
 * The variable swap itself is wrapped so it happens atomically from
 * the browser's point of view: transitions are suppressed, every
 * variable is written in one synchronous pass, and transitions are
 * only re-enabled once the new values have already painted.
 */
export function applyThemeToDom(theme: ITheme, root: HTMLElement = document.documentElement): void {
  const restoreTransitions = suppressTransitionsDuringSwap(root);

  const cssVars = buildCssVarMap(theme);

  for (const [name, value] of Object.entries(cssVars)) {
    if (value === undefined || value === null || value === '') {
      root.style.removeProperty(name);
      continue;
    }
    root.style.setProperty(name, value);
  }

  root.dataset.themeId = theme.manifest.id;

  restoreTransitions();
}
